import { Primitive } from "cesium";
import { Matrix4 } from "cesium";
import { PolylineGeometry } from "cesium";
import { ArcType } from "cesium";
import { Material } from "cesium";
import { ColorGeometryInstanceAttribute } from "cesium";
import { destroyObject } from "cesium";
import { GeometryInstance } from "cesium";
import { PolylineColorAppearance } from "cesium";
import { PolylineMaterialAppearance } from "cesium";
import { defined } from "cesium";
import { Check } from "cesium";
import { BoundingSphere } from "cesium";
import { Cartesian3 } from "cesium";
import { Color } from "cesium";
import { Math as CesiumMath } from "cesium";

/**
 * Options for constructing an AxisLinePrimitive
 */
interface AxisLinePrimitiveOptions {
  /** The positions of the polyline */
  positions: Cartesian3[];
  /** The color of the line */
  color: Color;
  /** Whether the primitive is visible */
  show?: boolean;
  /** An id for the primitive */
  id?: any;
  /** True if the polyline should loop */
  loop?: boolean;
  /** True if the arrow material should be used */
  arrow?: boolean;
  /** The width of the polyline */
  width?: number;
  /** True if a depthfail material should be used */
  depthFail?: boolean;
}

/**
 * A primitive for rendering 3D polylines with optional arrow styling and depth-fail rendering
 */
class AxisLinePrimitive {
  show: boolean;
  id?: any;

  private _width: number;
  private _color: Color;
  private _depthFailColor: Color;
  private _positions: Cartesian3[];
  private _arrow: boolean;
  private _depthFail: boolean;
  private _primitive?: Primitive;
  private _boundingSphere: BoundingSphere;
  private _transformedBoundingSphere: BoundingSphere;
  private _modelMatrix: Matrix4;
  private _update: boolean;

  constructor(options: AxisLinePrimitiveOptions) {
    Check.defined("options", options);
    Check.defined("options.positions", options.positions);
    Check.defined("options.color", options.color);

    this.show = options.show ?? true;
    this.id = options.id;

    let positions = options.positions;
    if (options.loop) {
      positions = positions.slice();
      positions.push(positions[0]);
    }
    const isArrow = options.arrow ?? false;
    this._width = defined(options.width) ? options.width! : isArrow ? 25 : 8;
    this._color = options.color;
    this._depthFailColor = options.color.withAlpha(0.3);
    this._positions = positions;
    this._arrow = isArrow;
    this._depthFail = options.depthFail ?? true;

    this._primitive = undefined;
    this._boundingSphere = BoundingSphere.fromPoints(positions);
    this._transformedBoundingSphere = BoundingSphere.clone(
      this._boundingSphere,
    );
    this._modelMatrix = Matrix4.clone(Matrix4.IDENTITY);

    this._update = true;
  }

  get modelMatrix(): Matrix4 {
    return this._modelMatrix;
  }

  set modelMatrix(value: Matrix4) {
    if (Matrix4.equalsEpsilon(value, this._modelMatrix, CesiumMath.EPSILON10)) {
      return;
    }
    this._modelMatrix = Matrix4.clone(value, this._modelMatrix);
    this._update = true;
  }

  get positions(): Cartesian3[] {
    return this._positions;
  }

  set positions(positions: Cartesian3[]) {
    this._positions = positions;
    this._update = true;
  }

  get color(): Color {
    return Color.clone(this._color);
  }

  get width(): number {
    return this._width;
  }

  get boundingVolume(): BoundingSphere {
    return this._transformedBoundingSphere;
  }

  update(frameState: any): void {
    if (!this.show) {
      return;
    }

    if (this._update) {
      this._update = false;
      if (this._primitive) {
        this._primitive.destroy();
        this._primitive = undefined;
      }

      const geometry = new PolylineGeometry({
        positions: this._positions,
        width: this._width,
        vertexFormat: PolylineMaterialAppearance.VERTEX_FORMAT,
        arcType: ArcType.NONE,
      });

      let appearance1: PolylineMaterialAppearance | PolylineColorAppearance;
      let appearance2:
        | PolylineMaterialAppearance
        | PolylineColorAppearance
        | undefined;

      if (this._arrow) {
        appearance1 = new PolylineMaterialAppearance({
          material: Material.fromType(Material.PolylineArrowType, {
            color: this._color,
          }),
        });
        if (this._depthFail) {
          appearance2 = new PolylineMaterialAppearance({
            material: Material.fromType(Material.PolylineArrowType, {
              color: this._depthFailColor,
            }),
          });
        }
      } else {
        appearance1 = new PolylineColorAppearance({
          translucent: this._color.alpha !== 1.0,
        });
        if (this._depthFail) {
          appearance2 = new PolylineColorAppearance({
            translucent: this._depthFailColor.alpha !== 1.0,
          });
        }
      }

      const modelMatrix = this._modelMatrix;
      this._primitive = new Primitive({
        geometryInstances: new GeometryInstance({
          geometry: geometry,
          attributes: {
            color: ColorGeometryInstanceAttribute.fromColor(this._color),
            depthFailColor: ColorGeometryInstanceAttribute.fromColor(
              this._depthFailColor,
            ),
          },
          id: this.id,
          modelMatrix: modelMatrix,
        }),
        appearance: appearance1,
        depthFailAppearance: appearance2,
        asynchronous: false,
      });
      this._transformedBoundingSphere = BoundingSphere.transform(
        this._boundingSphere,
        modelMatrix,
        this._transformedBoundingSphere,
      );
    }

    if (defined(this._primitive)) {
      (this._primitive as any).update(frameState);
    }
  }

  isDestroyed(): boolean {
    return false;
  }

  destroy(): void {
    if (this._primitive) {
      this._primitive.destroy();
      this._primitive = undefined;
    }
    return destroyObject(this);
  }
}

export default AxisLinePrimitive;
